import {Budget} from '../../interfaces';
import {AppUser} from '../../interfaces/app-user';



/**
 * Permissions management on Budget, implementation for:
 * https://docs.google.com/spreadsheets/d/1N9Gon7lznevQiBRH3QPlHkQDCYQPiXR9laPJO8Z2avY/edit?gid=47904532#gid=47904532
 */
export class BudgetModel {

    static isAssignedUser(user: AppUser, budget: Budget) {
        if (!budget) return false;
        if (!user) return false;
        return user.googleUid === budget.assignee?.googleUid;
    }

    static isEditableStatus(budget: Budget) {
        if (!budget) return false;
        return !['closed', 'archived', 'new'].includes(budget.status);
    }

    static areScopesSet(budget: Budget) {
        if (!budget) return false;
        if (budget.dummy) return true;
        if (budget.project?.internal) return true;
        return budget.scope?.length > 0;
    }

    private static _hasSpecialEditPermission(budget: Budget, user: AppUser): boolean {
        if (!budget?.authGrants || !user?.email) {
            return false;
        }
        const grant = budget.authGrants[user.email];
        return !!grant && grant !== 'no' && grant !== 'waiting'; // basically: when it's a Date/Timestamp.
    }

    static genericEditBudgetPermission(budget: Budget,
                                       user: AppUser,
                                       canEditBudget: boolean,
                                       canChangeOthersBudgetsWithoutPermissions: boolean): boolean {
        if (!budget || !BudgetModel.areScopesSet(budget)) {
            return false;
        }

        if (budget.status === 'archived') {
            return false;
        }

        if (budget.project?.internal) return true;

        const hasSpecialEditPermission = this._hasSpecialEditPermission(budget, user);

        // Antonio, Emanuela, Greta possono editare una commessa se hanno l'autorizzazione concessa
        if (budget.status === 'closed' && canChangeOthersBudgetsWithoutPermissions && hasSpecialEditPermission) {
            return true;
        }

        // Antonio, Emanuela, Greta possono editare un budget a prescindere. Anche chi ha il permesso può farlo.
        if (budget.status === 'assigned' && canChangeOthersBudgetsWithoutPermissions || hasSpecialEditPermission) {
            return true;
        }

        // L'utente non assegnato non può editare, a prescindere dallo status
        if (!this.isAssignedUser(user, budget)) return false;

        // Non si può mai editare una commessa. Si può sempre editare un budget se assegnati. Il permesso edit basico è obbligatorio.
        return BudgetModel.isEditableStatus(budget) && canEditBudget;
    }


    static isRateEditable(budget: Budget,
                          user: AppUser,
                          canGrantProjectAuth: boolean,
                          canEditBudget: boolean,
                          canChangeOthersBudgetsWithoutPermissions: boolean) {
        if (!budget || !BudgetModel.areScopesSet(budget)) {
            return false;
        }

        if (budget.status === 'archived') {
            return false;
        }

        if (budget.project?.internal) return true;

        const hasSpecialEditPermission = this._hasSpecialEditPermission(budget, user);

        // Antonio, Emanuela possono editare se hanno l'autorizzazione concessa
        if (budget.status === 'closed' && canGrantProjectAuth && hasSpecialEditPermission) {
            return true;
        }

        // Antonio, Emanuela, Greta possono editare a prescindere finché assigned
        if (budget.status === 'assigned' && (canGrantProjectAuth || canChangeOthersBudgetsWithoutPermissions)) {
            return true;
        }

        // L'utente non assegnato non può editare, a prescindere dallo status
        if (!this.isAssignedUser(user, budget)) return false;

        // Non si può mai editare a meno che assegnati e status ancora 'assigned'
        return BudgetModel.isEditableStatus(budget) && canEditBudget;
    }

    static isMarginGenExpAndProfitEditable(budget: Budget,
                                           user: AppUser,
                                           canGrantProjectAuth: boolean) {
        if (!budget || !BudgetModel.areScopesSet(budget)) {
            return false;
        }

        if (budget.status === 'archived') {
            return false;
        }

        if (budget.project?.internal) return true;

        const hasSpecialEditPermission = this._hasSpecialEditPermission(budget, user);

        // Antonio (canGrantProjectAuth), Emanuela (canGrantProjectAuth)
        // possono editare i margini se hanno l'autorizzazione concessa
        if (budget.status === 'closed' && canGrantProjectAuth && hasSpecialEditPermission) {
            return true;
        }

        // Antonio, Emanuela possono editare margini in un budget anche senza autorizzazioni
        if (budget.status === 'assigned' && canGrantProjectAuth) {
            return true;
        }

        // Tutti gli altri non possono mai, indipendentemente dallo status.
        return false;
    }
}
